import React from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, ShieldAlert, CalendarClock } from "lucide-react";
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Panel, Delta } from "./ui";

const IMG = { Berlina: "/fleet/berlina.jpg", "Van Executive": "/fleet/van.jpg", Minibus: "/fleet/minibus.jpg", "Bus GT": "/fleet/bus.jpg" };

export function Fleet({ items }) {
  const nav = useNavigate();
  return (
    <Panel title="Flotta" testid="nx-fleet" bodyClass="px-3 pb-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {items.map(f => (
          <button key={f.category} type="button" onClick={() => nav("/flotta")} data-testid={`nx-fleet-${f.category}`} className="rounded-xl overflow-hidden border border-white/8 bg-[#0A1120] text-left hover:border-[#D4A72C]/40">
            <img src={IMG[f.category]} alt="" className="w-full h-[72px] object-cover" />
            <div className="px-2.5 py-2"><div className="text-[12.5px] text-white">{f.category}</div><div className="text-[11px] text-emerald-400">{f.available} disponibil{f.available === 1 ? "e" : "i"} <span className="text-slate-500">/ {f.total}</span></div></div>
          </button>
        ))}
        {!items.length && <div className="col-span-4 text-sm text-slate-500 text-center py-8">Nessun veicolo in flotta</div>}
      </div>
      <div className="text-[9.5px] text-slate-600 mt-1.5">Foto illustrative di categoria</div>
    </Panel>
  );
}

const tone = (d) => d < 0 ? "text-rose-400" : d <= 15 ? "text-amber-300" : "text-slate-400";
export function Maintenance({ items }) {
  const nav = useNavigate();
  const Icon = (k) => k === "vehicle" ? Wrench : k === "driver" ? ShieldAlert : CalendarClock;
  return (
    <Panel title="Manutenzione e scadenze" badge={items.length || null} testid="nx-maint" bodyClass="px-3 pb-3">
      <div className="divide-y divide-white/5 max-h-[170px] overflow-y-auto">
        {items.slice(0, 8).map((e, i) => { const I = Icon(e.kind); return (
          <button key={i} type="button" onClick={() => nav(e.kind === "vehicle" ? "/flotta" : "/scadenze")} className="w-full flex items-center gap-2.5 py-2 text-left hover:bg-white/[0.02]" data-testid={`nx-maint-${i}`}>
            <I size={15} className="text-slate-300 shrink-0" />
            <span className="flex-1 min-w-0 text-[12px] text-white truncate">{e.subject} – {e.label || e.type || e.doc_type}</span>
            <span className={`text-[11px] whitespace-nowrap ${tone(e.days ?? 99)}`}>{e.days < 0 ? "Scaduta" : e.days === 0 ? "Scade oggi" : `Tra ${e.days} g`}</span>
          </button>); })}
        {!items.length && <div className="text-sm text-slate-500 text-center py-8" data-testid="nx-maint-empty">Nessuna scadenza nei prossimi 60 giorni</div>}
      </div>
    </Panel>
  );
}

const Mini = ({ label, children }) => <div className="rounded-xl border border-white/8 bg-[#0A1120] p-2.5 min-w-0"><div className="text-[10.5px] text-slate-400">{label}</div>{children}</div>;
const Donut = ({ v, color }) => (
  <div className="relative h-[62px]">
    <ResponsiveContainer><PieChart><Pie data={[{ v: v || 0 }, { v: 100 - (v || 0) }]} dataKey="v" innerRadius={20} outerRadius={28} startAngle={90} endAngle={-270} stroke="none">
      <Cell fill={color} /><Cell fill="#1E293B" /></Pie></PieChart></ResponsiveContainer>
  </div>
);
export function Analytics({ a }) {
  const s = a?.series || [];
  const last = s[s.length - 1], prev = s[s.length - 2];
  return (
    <Panel title="Analisi e performance" right={<span className="text-[11px] text-slate-400 border border-white/10 rounded-lg px-2 h-7 flex items-center">Ultimi 6 mesi</span>} testid="nx-analytics" bodyClass="px-3 pb-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Mini label="Fatturato"><div className="h-4"><Delta cur={last?.revenue} prev={prev?.revenue} /></div>
          <div className="h-[46px]"><ResponsiveContainer><BarChart data={s}><Bar dataKey="revenue" fill="#D4A72C" radius={[2, 2, 0, 0]} /></BarChart></ResponsiveContainer></div></Mini>
        <Mini label="Margine operativo"><div className="h-4"><Delta cur={last?.margin_pct} prev={prev?.margin_pct} points /></div>
          <div className="h-[46px]"><ResponsiveContainer><LineChart data={s}><Line dataKey="margin_pct" stroke="#34D399" dot={false} strokeWidth={2} connectNulls /></LineChart></ResponsiveContainer></div></Mini>
        <Mini label="Occupazione flotta"><div className="text-[13px] text-white font-mono-num" data-testid="nx-occupancy">{a?.occupancy_pct != null ? `${Math.round(a.occupancy_pct)}%` : "N/D"}</div><Donut v={a?.occupancy_pct} color="#BAE6FD" /></Mini>
        <Mini label="Clienti ricorrenti"><div className="text-[13px] text-white font-mono-num" data-testid="nx-returning">{a?.returning_pct != null ? `${Math.round(a.returning_pct)}%` : "N/D"}</div><Donut v={a?.returning_pct} color="#E9C46A" /></Mini>
      </div>
      <div className="text-[9.5px] text-slate-600 mt-1.5">Occupazione: giorni-veicolo con almeno un servizio nel mese. Ricorrenti: clienti del mese già serviti in passato.</div>
    </Panel>
  );
}
